import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ArrowLeft, Landmark } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import { api, bearerAuthConfig, formatApiErrorDetail } from '../core/api';
import { t } from '../core/i18n';
import { AFFILIATE_ADMIN } from '../constants/testIds';

const money = (minor) => `RM${((Number(minor) || 0) / 100).toFixed(2)}`;

export default function AdminAffiliateDashboard() {
  const navigate = useNavigate();
  const [state, setState] = useState('loading');
  const [queue, setQueue] = useState([]);
  const [detail, setDetail] = useState(null);
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const loadQueue = useCallback(async () => {
    setState('loading');
    try {
      await api.get('/affiliate/admin/access', bearerAuthConfig());
      const response = await api.get('/affiliate/admin/withdrawals?status=PENDING', bearerAuthConfig());
      setQueue(response.data.items || []);
      setState('ready');
    } catch (error) {
      setState(error.response?.status === 401 ? 'unauthenticated'
        : error.response?.status === 403 ? 'forbidden' : 'error');
    }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const openDetail = async (id) => {
    setBusy(true);
    try {
      const response = await api.get(`/affiliate/admin/withdrawals/${id}`, bearerAuthConfig());
      setDetail(response.data);
      setReference('');
      setReason('');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const closeDetail = () => {
    // Remove the only in-memory copy of unmasked bank data.
    setDetail(null);
    setReference('');
    setReason('');
  };

  const resolve = async (kind) => {
    if (!detail) return;
    setBusy(true);
    try {
      if (kind === 'paid') {
        await api.post(`/affiliate/admin/withdrawals/${detail.id}/paid`, {
          payoutReference: reference,
        }, bearerAuthConfig());
        toast.success(t('affiliateAdminPaidSuccess'));
      } else {
        await api.post(`/affiliate/admin/withdrawals/${detail.id}/reject`, {
          reason,
        }, bearerAuthConfig());
      }
      closeDetail();
      await loadQueue();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-cream px-5 py-8" data-testid={AFFILIATE_ADMIN.page}>
      <Toaster richColors position="top-center" />
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate('/')} className="min-h-[44px] font-body text-navy flex items-center gap-2">
          <ArrowLeft size={17} />{t('affiliateAdminBack')}
        </button>
        <div className="flex items-center gap-2 mt-4">
          <Landmark className="text-sky" />
          <h1 className="font-heading text-3xl font-bold text-navy">{t('affiliateAdminTitle')}</h1>
        </div>

        {state === 'loading' && <p className="font-body text-navy/60 mt-6">{t('affiliateSubmitting')}</p>}
        {state === 'unauthenticated' && <p className="font-body text-coral mt-6" data-testid={AFFILIATE_ADMIN.denied}>{t('affiliateAdminUnauthenticated')}</p>}
        {state === 'forbidden' && <p className="font-body text-coral mt-6" data-testid={AFFILIATE_ADMIN.denied}>{t('affiliateAdminDenied')}</p>}
        {state === 'error' && (
          <div className="mt-6"><p className="font-body text-coral">{t('affiliateLoadError')}</p>
            <button onClick={loadQueue} className="min-h-[44px] underline font-body text-navy">{t('replay')}</button>
          </div>
        )}

        {state === 'ready' && (
          <section className="bg-white rounded-3xl p-6 shadow-sm mt-6" data-testid={AFFILIATE_ADMIN.queue}>
            <h2 className="font-heading text-lg font-bold text-navy">{t('affiliateAdminQueue')}</h2>
            {queue.length === 0 ? (
              <p className="font-body text-sm text-navy/50 mt-4" data-testid={AFFILIATE_ADMIN.empty}>{t('affiliateAdminEmpty')}</p>
            ) : queue.map((item) => (
              <article key={item.id} data-testid={`affiliate-admin-row-${item.id}`}
                className="grid sm:grid-cols-[1fr_auto] gap-3 py-4 border-b border-navy/10 last:border-0">
                <div>
                  <p className="font-body font-bold text-navy">{t('affiliateAdminAmount')}: {money(item.amountMinor)}</p>
                  <p className="font-body text-xs text-navy/50">{t('affiliateAdminRequestedAt')}: {new Date(item.requestedAt).toLocaleString()}</p>
                  <p className="font-body text-xs text-navy/50">{t('affiliateAdminAge')}: {item.ageHours} {t('affiliateAdminHours')}</p>
                  {item.warning24Hours && !item.breach48Hours && (
                    <p className="font-body text-xs font-bold text-coral flex gap-1 mt-1" data-testid={`affiliate-admin-overdue-${item.id}`}>
                      <AlertTriangle size={14} />{t('affiliateAdminWarning')}
                    </p>
                  )}
                  {item.breach48Hours && (
                    <p className="font-body text-xs font-bold text-coral flex gap-1 mt-1" data-testid={`affiliate-admin-overdue-${item.id}`}>
                      <AlertTriangle size={14} />{t('affiliateAdminBreach')}
                    </p>
                  )}
                </div>
                <button disabled={busy} onClick={() => openDetail(item.id)}
                  data-testid={`affiliate-admin-open-${item.id}`}
                  className="self-center min-h-[44px] rounded-full bg-navy text-white px-5 font-body font-bold disabled:opacity-50">
                  {t('affiliateAdminOpen')}
                </button>
              </article>
            ))}
          </section>
        )}

        {detail && (
          <section className="fixed inset-0 z-50 bg-navy/50 p-5 overflow-y-auto" aria-modal="true" role="dialog">
            <div className="max-w-xl mx-auto bg-white rounded-3xl p-6 my-8" data-testid={AFFILIATE_ADMIN.detail}>
              <h2 className="font-heading text-xl font-bold text-navy">{t('affiliateAdminDetail')}</h2>
              <p className="font-body text-sm text-navy mt-3">{t('affiliateAdminAmount')}: <b>{money(detail.amountMinor)}</b></p>
              <dl className="bg-cream rounded-2xl p-4 mt-4 space-y-2 font-body text-sm text-navy">
                <div><dt className="text-navy/50">{t('affiliateHolder')}</dt><dd data-testid={AFFILIATE_ADMIN.bankHolder}>{detail.bankDetails.accountHolderName}</dd></div>
                <div><dt className="text-navy/50">{t('affiliateBank')}</dt><dd data-testid={AFFILIATE_ADMIN.bankName}>{detail.bankDetails.bankName}</dd></div>
                <div><dt className="text-navy/50">{t('affiliateAccount')}</dt><dd data-testid={AFFILIATE_ADMIN.accountNumber}>{detail.bankDetails.accountNumber}</dd></div>
              </dl>
              <label className="block font-body text-sm font-bold text-navy mt-4">
                {t('affiliateAdminReference')}
                <input data-testid={AFFILIATE_ADMIN.referenceInput} value={reference} onChange={(event) => setReference(event.target.value)}
                  autoComplete="off" className="mt-1 h-12 w-full rounded-xl border-2 border-navy/10 px-4" />
              </label>
              <button data-testid={AFFILIATE_ADMIN.markPaidButton} disabled={busy || reference.trim().length < 2}
                onClick={() => resolve('paid')} className="mt-3 min-h-[44px] rounded-full bg-leaf text-navy px-5 font-body font-bold disabled:opacity-50">
                {t('affiliateAdminMarkPaid')}
              </button>
              <label className="block font-body text-sm font-bold text-navy mt-5">
                {t('affiliateAdminRejectReason')}
                <textarea data-testid={AFFILIATE_ADMIN.rejectReasonInput} value={reason} onChange={(event) => setReason(event.target.value)}
                  className="mt-1 min-h-[90px] w-full rounded-xl border-2 border-navy/10 p-4" />
              </label>
              <button data-testid={AFFILIATE_ADMIN.rejectButton} disabled={busy || reason.trim().length < 3}
                onClick={() => resolve('reject')} className="mt-3 min-h-[44px] rounded-full bg-coral text-white px-5 font-body font-bold disabled:opacity-50">
                {t('affiliateAdminReject')}
              </button>
              <button data-testid={AFFILIATE_ADMIN.closeDetailButton} onClick={closeDetail}
                className="mt-5 min-h-[44px] block underline font-body text-navy/60">{t('affiliateAdminClose')}</button>
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
